import {isEqual} from 'lodash';
import {
  createParser,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import {useMemo} from 'react';

import {INTERPOLATOR, MOSAICKING_ORDER} from '@src/constants/analyze';

export type RangeTuple = [number, number];

export const SETTINGS_URL_KEY_MAP = {
  mosaickingOrder: 'mosaicking',
  upsampling: 'upsampling',
  downsampling: 'downsampling',
};

export const EFFECTS_URL_KEY_MAP = {
  gain: 'gain',
  gamma: 'gamma',
  redRange: 'rr',
  greenRange: 'rg',
  blueRange: 'rb',
} as const;

export const URL_KEY_MAP = {
  configurationId: 'configuration',
  collectionId: 'collection',
  layerId: 'layer',
  ...SETTINGS_URL_KEY_MAP,
  ...EFFECTS_URL_KEY_MAP,
} as const;

type SettingsProps = {
  mosaickingOrder?: MOSAICKING_ORDER;
  upsampling?: INTERPOLATOR;
  downsampling?: INTERPOLATOR;
};

type EffectsProps = SettingsProps & {
  gain?: number;
  gamma?: number;
  redRange?: RangeTuple;
  greenRange?: RangeTuple;
  blueRange?: RangeTuple;
};

export const SETTINGS_EFFECTS_DEFAULTS: Required<EffectsProps> = {
  mosaickingOrder: MOSAICKING_ORDER.mostRecent,
  upsampling: INTERPOLATOR.bilinear,
  downsampling: INTERPOLATOR.bilinear,
  gain: 1,
  gamma: 1,
  redRange: [0, 1],
  greenRange: [0, 1],
  blueRange: [0, 1],
} as const;

const isValidFloat = (value: string, min: number, max: number) => {
  const floatValue = parseFloat(value);
  const isValid =
    Number.isFinite(floatValue) && floatValue >= min && floatValue <= max;
  return isValid ? floatValue : null;
};

const createParseAsEffect = (min: number, max: number) =>
  createParser({
    parse(queryValue) {
      return isValidFloat(queryValue, min, max);
    },
    serialize(value) {
      return value.toFixed(2);
    },
  });

const createParseAsFloatRange = () =>
  createParser({
    parse(queryValue): RangeTuple | null {
      const values = queryValue.split('/');
      if (values.length !== 2) {
        return null;
      }
      const parsedValues = values
        .map(v => isValidFloat(v, 0, 1))
        .filter(Boolean);

      return parsedValues.length === 2 ? (parsedValues as RangeTuple) : null;
    },
    serialize(value) {
      return `${value[0]?.toFixed(2)}/${value[1]?.toFixed(2)}`;
    },
  });

const useVisualizationParams = () => {
  const [
    {
      configurationId,
      collectionId,
      layerId,
      mosaickingOrder,
      upsampling,
      downsampling,
      gain,
      gamma,
      redRange,
      greenRange,
      blueRange,
    },
    setVisualizationParams,
  ] = useQueryStates(
    {
      configurationId: parseAsString.withDefault(''),
      collectionId: parseAsString.withDefault(''),
      layerId: parseAsString.withDefault(''),
      mosaickingOrder: parseAsStringEnum<MOSAICKING_ORDER>(
        Object.values(MOSAICKING_ORDER),
      ).withDefault(SETTINGS_EFFECTS_DEFAULTS.mosaickingOrder),
      upsampling: parseAsStringEnum<INTERPOLATOR>(
        Object.values(INTERPOLATOR),
      ).withDefault(SETTINGS_EFFECTS_DEFAULTS.upsampling),
      downsampling: parseAsStringEnum<INTERPOLATOR>(
        Object.values(INTERPOLATOR),
      ).withDefault(SETTINGS_EFFECTS_DEFAULTS.downsampling),
      gain: createParseAsEffect(0, 10).withDefault(
        SETTINGS_EFFECTS_DEFAULTS.gain,
      ),
      gamma: createParseAsEffect(0, 10).withDefault(
        SETTINGS_EFFECTS_DEFAULTS.gamma,
      ),
      redRange: createParseAsFloatRange().withDefault(
        SETTINGS_EFFECTS_DEFAULTS.redRange,
      ),
      greenRange: createParseAsFloatRange().withDefault(
        SETTINGS_EFFECTS_DEFAULTS.greenRange,
      ),
      blueRange: createParseAsFloatRange().withDefault(
        SETTINGS_EFFECTS_DEFAULTS.blueRange,
      ),
    },
    {
      urlKeys: URL_KEY_MAP,
    },
  );

  const effectsProps = {
    mosaickingOrder,
    upsampling,
    downsampling,
    gain,
    gamma,
    redRange,
    greenRange,
    blueRange,
  };
  const settingsEffectsApplies = !isEqual(
    effectsProps,
    SETTINGS_EFFECTS_DEFAULTS,
  );

  const setters = useMemo(
    () => ({
      setConfigurationId: (id: string) => {
        setVisualizationParams(() => ({
          configurationId: id,
          collectionId: '',
          layerId: '',
        }));
      },
      setCollectionId: (id: string) => {
        setVisualizationParams(prev => ({
          ...prev,
          collectionId: id,
          layerId: '',
        }));
      },
      setLayerId: (id: string) => {
        setVisualizationParams(prev => ({
          ...prev,
          layerId: id,
        }));
      },
      setParams: (props: EffectsProps) => {
        setVisualizationParams(prev => ({
          ...prev,
          ...props,
        }));
      },
      resetVisualizationParams: () => {
        setVisualizationParams({
          configurationId: '',
          layerId: '',
          mosaickingOrder: SETTINGS_EFFECTS_DEFAULTS.mosaickingOrder,
          gain: SETTINGS_EFFECTS_DEFAULTS.gain,
          gamma: SETTINGS_EFFECTS_DEFAULTS.gamma,
          redRange: SETTINGS_EFFECTS_DEFAULTS.redRange,
          blueRange: SETTINGS_EFFECTS_DEFAULTS.blueRange,
          greenRange: SETTINGS_EFFECTS_DEFAULTS.greenRange,
          upsampling: INTERPOLATOR.bilinear,
          downsampling: INTERPOLATOR.bilinear,
        });
      },
    }),
    [setVisualizationParams],
  );

  return {
    configurationId,
    collectionId,
    layerId,
    mosaickingOrder,
    upsampling,
    downsampling,
    gain,
    gamma,
    redRange,
    greenRange,
    blueRange,
    settingsEffectsApplies,
    ...setters,
  };
};

export default useVisualizationParams;
